import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

// ── Event names ──────────────────────────────────────────────────────────────
const VALIDATION_ERROR_ON_EXIT = 'MV:Usability - Validation Error on Exit';
const SAVE_VALIDATION = 'MV:Usability - Save Validation';
const PQ_PRELOADING = 'MV:Usability - PQ Pre-loading';
// Generic, application-agnostic event (the app identifier is already on the
// PageAction), so it can be reused from Sales View. PascalCase to match the
// sibling redux telemetry events (ReduxAction, SelectorDelta, StateSnapshotDelta).
const OUT_OF_ORDER_PAYMENT_RESOLVED = 'OutOfOrderPaymentResolved';
// Emitted when the payment sequence guard identifies a superseded response and forwards it anyway — because a
// registered pending operation still needs it, or because the trigger is un-discardable by construction (an
// exempt entry, a dealer-override follow-up). Non-zero by design, and the other half of the discard metric:
// facet by `reason`, then by `selectorName` / `triggerType`. A NEW selectorName, or a volume jump on an
// existing one, is what names a missed exemption. See paymentSequenceRegistry.ts.
const PAYMENT_SEQUENCE_PASS_THROUGH = 'PaymentSequencePassThrough';
// Emitted every time the payment sequence guard actually discards a superseded response. This is the
// rollout signal for the guard: OUT_OF_ORDER_PAYMENT_RESOLVED measures how often the underlying race
// happens (its middleware runs upstream of the guard, so it still sees discarded responses), and this
// measures how often the guard acted. The race counter alone cannot tell a working guard from one
// comparing the wrong lane, since both drive it to zero.
const PAYMENT_SEQUENCE_DISCARDED = 'PaymentSequenceDiscarded';
// Emitted every time guardStaleTermIncentiveWrites actually overwrites a term's incentive/rebate fields
// (i.e. patchedById ends up non-empty for this response) — not on every call, since most calls are a
// no-op mark update. This is the rollout signal for that guard specifically: without it there is no way
// to tell, in production, whether it fires once a week or on every payment, or whether the term-id
// re-keying gap (see paymentSequenceMiddleware.ts) makes it a silent no-op for a given deal type.
const TERM_INCENTIVE_RESTORED = 'PaymentSequenceTermIncentiveRestored';

// ── Shared context type ──────────────────────────────────────────────────────
export interface UsabilityContext {
    sessionId?: string;
    traceId?: string;
    dealXgId?: string;
    dealType?: string;
    mmdDealerId?: string | number;
    vin?: string;
}

// ── Exit validation tracking ─────────────────────────────────────────────────

export interface ExitValidationEventData {
    guardType: 'exit' | 'save';
    /** Map of entity name → error count (only entities with count > 0) */
    entitiesWithErrors: Record<string, number>;
    /** Raw validation engine error tree for field-level analysis */
    errors?: FormErrorNode | null | undefined;
}

export const trackValidationErrorOnExit = (data: ExitValidationEventData, context: UsabilityContext): void => {
    const entries = Object.entries(data.entitiesWithErrors).filter(([, count]) => count > 0);
    const errorCategories = entries.map(([entity]) => entity).join(',');
    const errorCountByCategory = entries.map(([entity, count]) => `${entity}:${count}`).join(',');

    const { missingFields } = data.errors ? collectValidationErrors(data.errors) : { missingFields: [] };

    logNewRelicPageAction(VALIDATION_ERROR_ON_EXIT, {
        guardType: data.guardType,
        totalErrorCount: missingFields.length,
        missingFields: missingFields.join(','),
        errorCategories,
        errorCountByCategory,
        ...context
    });
};

// ── Save validation tracking ─────────────────────────────────────────────────

/** Leaf node produced by ValidationEngine.getFormErrorState() */
export interface FormErrorLeaf {
    hasError: boolean;
    errorMessage?: string;
}

/** Recursive error-state tree returned by ValidationEngine.getFormErrorState() */
export type FormErrorNode = FormErrorLeaf | { [key: string]: FormErrorNode };

const isNumericPathSegment = (segment: string): boolean => /^\d+$/.test(segment);

/**
 * Recursively collect leaf-level errors from the validation engine's error state.
 * Returns missing field paths (e.g. "buyer.email") and per-category counts.
 */
export const collectValidationErrors = (
    errors: FormErrorNode | null | undefined
): { missingFields: string[]; missingCategories: string[]; errorCountByCategory: string } => {
    const missingFields: string[] = [];
    const missingCategories: string[] = [];
    const categoryFieldCount: Record<string, number> = {};

    const collectLeafErrors = (obj: FormErrorNode | null | undefined, prefix = ''): void => {
        if (obj == null || typeof obj !== 'object') return;
        const record = obj as Record<string, unknown>;
        for (const key of Object.keys(record)) {
            const val = record[key] as FormErrorNode | null | undefined;
            const path = prefix ? `${prefix}.${key}` : key;
            if (val?.hasError === true) {
                const cleanPath = path
                    .split('.')
                    .filter((seg) => !isNumericPathSegment(seg))
                    .join('.');
                if (!missingFields.includes(cleanPath)) {
                    missingFields.push(cleanPath);
                }
                const category = path.split('.')[0];
                categoryFieldCount[category] = (categoryFieldCount[category] || 0) + 1;
                if (!missingCategories.includes(category)) {
                    missingCategories.push(category);
                }
            } else if (typeof val === 'object' && val !== null) {
                collectLeafErrors(val, path);
            }
        }
    };
    collectLeafErrors(errors);

    const errorCountByCategory = missingCategories.map((cat) => `${cat}:${categoryFieldCount[cat]}`).join(',');

    return { missingFields, missingCategories, errorCountByCategory };
};

export interface SaveValidationEventData {
    hasErrors: boolean;
    errors: FormErrorNode | null | undefined;
}

// ── PQ pre-loading tracking ──────────────────────────────────────────────────

export interface PQPreloadingEventData {
    offerType: string;
}

export const trackPQPreloading = (data: PQPreloadingEventData, context: UsabilityContext): void => {
    logNewRelicPageAction(PQ_PRELOADING, {
        offerType: data.offerType,
        ...context
    });
};

// ── Out-of-order payment resolution tracking ─────────────────────────────────

export interface OutOfOrderPaymentResolvedEventData {
    /** actionIndex of the edit whose (stale) payment response just landed */
    resolvedActionIndex: number;
    /** Highest edit actionIndex whose payment response had already landed */
    latestActionIndex: number;
    /** latestActionIndex − resolvedActionIndex: how far behind the stale response was */
    actionIndexGap: number;
    /** The resolving action type (PAYMENT_RECEIVED or PAYMENT_FAILURE) */
    resolvedType: string;
    /** correlationId of the triggering edit — joins to ReduxAction / SelectorDelta / StateSnapshotDelta */
    correlationId?: string;
}

export const trackOutOfOrderPaymentResolved = (data: OutOfOrderPaymentResolvedEventData, context: UsabilityContext): void => {
    logNewRelicPageAction(OUT_OF_ORDER_PAYMENT_RESOLVED, {
        resolvedActionIndex: data.resolvedActionIndex,
        latestActionIndex: data.latestActionIndex,
        actionIndexGap: data.actionIndexGap,
        resolvedType: data.resolvedType,
        correlationId: data.correlationId,
        storeId: 'dash-store',
        ...context
    });
};

// ── Payment sequence guard pass-through tracking ──────────────────────────────

export interface PaymentSequencePassThroughEventData {
    /**
     * Why the guard could not act: a registered pending operation needed the response
     * (`pendingOperation`), or the trigger is structurally un-discardable — an exempt entry
     * (`exemptTrigger`) or a dealer-override follow-up the superseding call did not inherit
     * (`dealerOverrideFollowUp`).
     *
     * Group by this first. Without it, a low `PaymentSequenceDiscarded` count cannot be read: "no race
     * happened" and "the race happened where the guard has no reach" look identical.
     */
    reason: 'exemptTrigger' | 'dealerOverrideFollowUp' | 'pendingOperation';
    /** Registered name of the pending operation that forced the pass-through — `pendingOperation` only */
    selectorName?: string;
    /** meta.paymentSeq of the superseded response that was forwarded anyway */
    supersededSeq: number;
    /** Latest sequence dispatched in the same lane */
    latestSeq: number;
    /** The resolving action type (PAYMENT_RECEIVED, PAYMENT_FAILURE, ...) */
    resolvedType: string;
    /** Lane the response belongs to (`${offerType}|main`) */
    lane: string;
    /** Type of the triggering action whose response was forwarded — names the workflow that raced */
    triggerType: string;
}

export const trackPaymentSequencePassThrough = (data: PaymentSequencePassThroughEventData, context: UsabilityContext): void => {
    logNewRelicPageAction(PAYMENT_SEQUENCE_PASS_THROUGH, {
        reason: data.reason,
        // Omitted rather than sent as undefined for the non-selector reasons, so a NRQL facet on this
        // attribute holds only real registered names.
        ...(data.selectorName ? { selectorName: data.selectorName } : {}),
        supersededSeq: data.supersededSeq,
        latestSeq: data.latestSeq,
        seqGap: data.latestSeq - data.supersededSeq,
        resolvedType: data.resolvedType,
        lane: data.lane,
        triggerType: data.triggerType,
        storeId: 'dash-store',
        ...context
    });
};

// ── Payment sequence guard discard tracking ───────────────────────────────────

export interface PaymentSequenceDiscardedEventData {
    /** meta.paymentSeq of the response that was discarded */
    supersededSeq: number;
    /** Latest sequence dispatched in the same lane */
    latestSeq: number;
    /** The resolving action type (PAYMENT_RECEIVED, PAYMENT_FAILURE, ...) */
    resolvedType: string;
    /** Lane the response belonged to (`${offerType}|main`) */
    lane: string;
    /** Type of the triggering action whose response was discarded — names the workflow that raced */
    triggerType: string;
}

export const trackPaymentSequenceDiscarded = (data: PaymentSequenceDiscardedEventData, context: UsabilityContext): void => {
    logNewRelicPageAction(PAYMENT_SEQUENCE_DISCARDED, {
        supersededSeq: data.supersededSeq,
        latestSeq: data.latestSeq,
        seqGap: data.latestSeq - data.supersededSeq,
        resolvedType: data.resolvedType,
        lane: data.lane,
        triggerType: data.triggerType,
        storeId: 'dash-store',
        ...context
    });
};

// ── Term-level incentive/rebate guard restore tracking ────────────────────────

export interface TermIncentiveRestoredEventData {
    /** Term id whose fields were overwritten */
    termId: string;
    /** meta.paymentSeq of the stale response this restore acted on */
    supersededSeq: number;
    /** The term's recorded high-water-mark seq — the later-dispatched request that beat this response */
    highestSeq: number;
    /** The resolving action type — always PAYMENT_RECEIVED, kept for symmetry with the sibling events */
    resolvedType: string;
    /** Type of the triggering action whose response was spliced */
    triggerType: string;
    /** Comma-joined TERM_INCENTIVE_FIELDS names that were actually overwritten (a field absent from the
     *  current term is left alone, not counted here — see the "never deletes" note on the guard) */
    restoredFields: string;
    /**
     * `Object.keys(action.payload.terms.byId).length` for the RESPONSE BEING RESTORED — how many term
     * ids that response's own payload enumerated. Answers, in production rather than only by static
     * trace, whether a response that collides with (and gets reverted by) another response's mark is
     * a single-cell answer or part of a many-term sweep (e.g. `SELECTED_CREDIT_TIER`'s
     * `termStrategy: 'all'`). A value greater than 1 here is the signal to look at more closely: it
     * means this restore's mark could have been raised by a request that was never actually asking
     * about this specific term id, only pricing it as a side effect of pricing everything.
     */
    termCountInResponse: number;
}

export const trackTermIncentiveRestored = (data: TermIncentiveRestoredEventData, context: UsabilityContext): void => {
    logNewRelicPageAction(TERM_INCENTIVE_RESTORED, {
        termId: data.termId,
        supersededSeq: data.supersededSeq,
        highestSeq: data.highestSeq,
        seqGap: data.highestSeq - data.supersededSeq,
        resolvedType: data.resolvedType,
        triggerType: data.triggerType,
        restoredFields: data.restoredFields,
        termCountInResponse: data.termCountInResponse,
        storeId: 'dash-store',
        ...context
    });
};

// ── Save validation tracking ─────────────────────────────────────────────────

export const trackSaveValidation = (data: SaveValidationEventData, context: UsabilityContext): void => {
    const { missingFields, missingCategories, errorCountByCategory } =
        data.hasErrors && data.errors
            ? collectValidationErrors(data.errors)
            : { missingFields: [], missingCategories: [], errorCountByCategory: '' };

    logNewRelicPageAction(SAVE_VALIDATION, {
        hasErrors: data.hasErrors,
        totalErrorCount: missingFields.length || undefined,
        missingFields: missingFields.join(',') || undefined,
        missingCategories: missingCategories.join(',') || undefined,
        errorCountByCategory: errorCountByCategory || undefined,
        ...context
    });
};
